import { GLTexture } from "../texture.js"
import { Entity } from "./Entity.js"
import { V3, V4 } from "./Vector.js"

export enum TexChain {
  world = 0,
  model
} 

export type UnloadedModel = {
  name: string
  needload: true
}

export type EFrags = {
  leafnext: EFrags | null
  entity: Entity
}

export type ClipNode = {
  planenum: number;
  children: [number, number];
}

export type PolyChain = {
  next: PolyChain | null
  verts: [...V3, number, number, number, number][]
  numverts: number
}

export type Texture = {
  name: string;
  width: number;
  height: number;
  texturenum: WebGLTexture
  texturechains: Record<TexChain, Face>;
  turbulent: boolean;
  sky: boolean;
  anims: number[]
  alternate_anims: number[]
  anim_base: number
  anim_frame: number
}

export type Hull = {
  firstclipnode?: number;
  lastclipnode: number;
  planes: Plane[];
  clip_mins: V3;
  clip_maxs: V3;
  clipnodes: ClipNode[]
  // Flat clipnode/plane arrays, derived read-only at load. Absent on the box
  // hull, whose plane dists are mutated per query (hullForBox) — the clip
  // functions branch on `flat != null`.
  flat?: HullFlat
}

// SoA mirror of a hull's clipnodes plus the owning model's plane SoA.
// clipChildren values: >= 0 next clipnode index, < 0 CONTENTS_* value,
// exactly as stored in ClipNode.children.
export type HullFlat = {
  clipPlane: Int32Array
  clipChildren: Int32Array
  planeNormal: Float64Array
  planeDist: Float64Array
  planeType: Uint8Array
}


export type Node = {
  num: number;
  contents: number;
  planenum?: number;
  children: [Node | Leaf, Node | Leaf];
  childrenNum?: [number, number];
  mins: V3;
  maxs: V3;
  firstface: number;
  numfaces: number;
  cmds: number[];
  plane: Plane;
  parent?: Node
  // per-frame mutable state lives in the owning Model's nodeMarkvisframe,
  // indexed by `num` — see Model type
}

export type Leaf = {
  num: number;
  contents: number;
  visofs: number;
  mins: V3;
  maxs: V3;
  firstmarksurface: number;
  nummarksurfaces: number;
  ambient_level: V4;
  cmds: [],
  skychain: 0,
  waterchain: 0
  parent: Node
  efrags: EFrags
  // per-frame mutable state lives in the owning Model's leafMarkvisframe,
  // indexed by `num` — see Model type
}
export type Face = {
  num: number;
  plane: Plane;
  side: number;
  firstedge: number;
  numedges: number;
  texinfo: number;
  styles: number[];
  lightofs: number;
  mins: V3;
  maxs: V3;
  texture: number;
  flags: number;
  sky: boolean;
  turbulent: boolean;
  texturemins: [number, number]
  extents: [number, number];
  polys: PolyChain
  light_t: number
  light_s: number
  texturechain: Face
  lightmaptexturenum: number
  vbo_firstvert: number,
  // per-frame mutable state lives in the owning Model's surfVisframe /
  // surfVisibleFrame, indexed by `num`
}

export type NodeLeaf = Node | Leaf
export type Edge = [number, number]

export type Plane = {
  normal: V3;
  dist: number;
  type: number;
  signbits: number
}

export type TexInfo = {
  vecs: [V4, V4]
  texture: number;
  flags: number;
}


export type Skin = {
  group: false;
  texturenum: GLTexture
  playertexture: WebGLTexture
  interval: number
}

export type SkinGroup = {
  group: true,
  skins?: Skin[]
}

export type FrameVert = {
  lightnormalindex: number
  v: V3
}

export type AliasFrameGroup = {
  type: 'alias'
  group: true,
  bboxmin: V3,
  bboxmax: V3,
  frames: AliasFrame[]
}

export type AliasFrame = {
  type: 'alias'
  group: false,
  name: string
  numposes: number,
  bboxmin: V3,
  bboxmax: V3,
  interval: number,
  v: FrameVert[],
  cmdofs: number
  // Sprite?
}

export type SpriteFrame = {
  type: 'sprite'
  group: false,
  origin: [number, number]
  width: number
  height: number
  texturenum: WebGLTexture
  interval: number,
}
export type SpriteFrameGroup = {
  type: 'sprite'
  group: true,
  frames: SpriteFrame[]
}

export type Frame = AliasFrame | AliasFrameGroup | SpriteFrame | SpriteFrameGroup

export type StVert = {
  onseam: boolean,
  s: number;
  t: number
}
export type Triangle = {
  facesfront: boolean;
  vertindex: V3
}
export type Model = {
  name: string
  type: number;
  player: boolean;
  firstface: number
  numfaces: number;
  cmds: WebGLBuffer;

  submodels: Model[];
  visleafs: number;
  numleafs: number;
  mins: V3;
  maxs: V3;
  hulls: Hull[];
  textures: Texture[];
  lightdata: Uint8Array;
  visdata: Uint8Array;
  entities: string;
  planes: Plane[];
  nodes: Node[];
  clipnodes: ClipNode[]
  leafs: Leaf[]
  edges: Edge[]
  texinfo: TexInfo[]
  faces: Face[]
  surfedges: number[]
  marksurfaces: number[]
  skinwidth: number
  skinheight: number
  radius: number
  scale: V3;
  scale_origin: V3;
  boundingradius: number;
  oriented: boolean
  width: number
  height: number

  numtris: number
  triangles: Triangle[]

  random: boolean
  flags: number
  nolerp: boolean // true for models on Ironwail's r_nolerp_list (flames, torches, view weapons that shouldn't blend)

  numskins: number;
  skins: (Skin | SkinGroup)[]

  numframes: number;
  frames: Frame[]

  numverts: number;
  vertexes: V3[];

  stverts: StVert[]
  submodel: boolean
  origin: V3

  // Dense per-frame render state, indexed by node/leaf/surf.num — flat and
  // shared with submodels (which share the same underlying faces array) so
  // surfVisframe/surfVisibleFrame indices line up across model and submodels[i].
  nodeMarkvisframe: Int32Array
  leafMarkvisframe: Int32Array
  surfVisframe: Int32Array
  // Stamped with r.state.frustumFrame by markWorldFrustum for every marksurface
  // reachable from an in-PVS, in-frustum node whose owning node also faces the
  // camera (see surfPlaneBack) — consumers compare against the current frame's
  // stamp instead of re-testing each surface's own plane/bbox.
  surfVisibleFrame: Int32Array

  // SoA mirror of the BSP tree, derived read-only at load and verified
  // against the object graph (verifyFlatBsp). Child values (in nodePacked):
  // >= 0 node index, < 0 encodes leaf -1-leafIndex (C convention).
  // nodeParent/leafParent: parent node index, -1 = root/none.
  nodePlane: Int32Array
  nodeParent: Int32Array
  // markWorldFrustum's per-node record: one 16-element (64B) slot per node,
  // two typed-array views over the same buffer so a visit costs 1-2 cache
  // lines instead of touching 7 separate arrays. base = idx * 16.
  //   f32[base+0..2]  mins xyz          f32[base+9]   plane dist
  //   f32[base+3..5]  maxs xyz          i32[base+10]  plane type
  //   f32[base+6..8]  plane normal xyz  i32[base+11]  firstFace
  //   i32[base+12]    numFaces          i32[base+13]  child0
  //   i32[base+14]    child1            [base+15]     unused (padding)
  // Does NOT include nodeMarkvisframe: that array is rewritten per view-leaf
  // chain rebuild by markAncestorsVisible, so it stays a separate array —
  // the walk reads it first as a PVS gate and only touches this record for
  // nodes that pass, which also means culled nodes skip this cache line.
  nodePacked: Float32Array
  nodePackedI32: Int32Array
  // Deepest node path (root = depth 1); sizes markWorldFrustum's DFS stack.
  bspMaxDepth: number
  leafContents: Int32Array
  leafParent: Int32Array
  planeNormal: Float64Array
  planeDist: Float64Array
  planeType: Uint8Array
  planeSignbits: Uint8Array
  // 1 where the face is on the back side of its owning node's plane
  // (def.SURF.planeback); indexed by Face.num.
  surfPlaneBack: Uint8Array

  // Flat per-face mirrors of Face.flags/lightmaptexturenum, indexed by
  // Face.num, so per-frame draw loops never dereference Face objects.
  // surfFlags is filled at load; surfLightmapPage once lightmap pages are
  // assigned (lightmap.createSurfaceLightmap).
  surfFlags: Int32Array
  surfLightmapPage: Int32Array

  // Prebuilt triangle-fan indices for the world VBO (same generation as the
  // old triangleIndicesForSurf), concatenated across all faces. ofs/count
  // are BSP-derived (known at load); the index values in surfIndexData are
  // filled once vbo_firstvert is assigned (r.buildModelVertexBuffer).
  surfIndexData: Uint32Array
  surfIndexOfs: Int32Array
  surfIndexCount: Int32Array

  // Flat mirror of the world (TexChain.world) texture chains, rebuilt in
  // r.markSurfaces whenever the linked chains are rebuilt (PVS change).
  // worldChainFaces holds Face.num values grouped by texture; texture i's
  // range is [worldChainOfs[i], worldChainOfs[i] + worldChainCount[i]).
  worldChainFaces: Int32Array
  worldChainOfs: Int32Array
  worldChainCount: Int32Array

  // Deduped texture indices (into `textures`) referenced by this model's own
  // faces (faces[firstface..firstface+numfaces)). Lets per-entity texture
  // chain scans skip the full world texture array for brush entities, which
  // typically touch only a handful of textures. Built once at load time.
  usedTextures: Int32Array
}
